import { z } from 'zod';
import { Currency, SourceSystem } from '../enums.js';
import { DecimalString, IsoDate, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';

/**
 * S14-BE.1 — Payment + PaymentAllocation. Mirror-pattern ERPNext Payment Entry.
 */

export const PaymentDirection = z.enum(['inflow', 'outflow']);
export type PaymentDirection = z.infer<typeof PaymentDirection>;

export const PaymentMethod = z.enum(['bank_transfer', 'cash', 'card', 'offset', 'other']);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

/** `pushing` — B74 (Ф6, §9.4 п. 7): идёт отправка в 1С по заявке уровня 3. */
export const PaymentState = z.enum(['draft', 'posted', 'cancelled', 'pushing']);
export type PaymentState = z.infer<typeof PaymentState>;

export const Payment = z.object({
  id: UuidString,
  entity_id: UuidString,
  direction: PaymentDirection,
  method: PaymentMethod,
  state: PaymentState,
  counterparty_id: UuidString,
  payment_date: IsoDate,
  amount: MoneyAmount,
  currency: Currency,
  fx_rate_to_kzt: DecimalString.default('1'),
  bank_account_ref: z.string().nullable(),
  external_ref: z.string().nullable(),
  notes_ru: z.string().nullable(),
  onec_ref: z.string().nullable(),
  source_system: SourceSystem,
  /**
   * 0025 — cash-flow taxonomy from bank-statement / Adesk import.
   * Optional / nullable для backward-compat (existing payments не имеют этих полей).
   */
  cf_article: z.string().nullable().optional(),
  cf_group: z.string().nullable().optional(),
  project_tag: z.string().nullable().optional(),
  /** 0025 — internal treasury transfer (между счетами одного юрлица) — исключается из CF/P&L. */
  internal_transfer: z.boolean().optional(),
  /** integrations B17 — seed reconcile status (live/superseded/seed_only/ambiguous). */
  reconcile_status: z.enum(['live', 'superseded', 'seed_only', 'ambiguous']).optional(),
  created_at: IsoDateTime,
  // EFE-13/14: updated_at для optimistic concurrency + detail page footer.
  updated_at: IsoDateTime.optional(),
  posted_at: IsoDateTime.nullable(),
  created_by: UuidString,
});
export type Payment = z.infer<typeof Payment>;

export const PaymentAllocationMethod = z.enum(['fifo_auto', 'manual', 'settlement_offset']);
export type PaymentAllocationMethod = z.infer<typeof PaymentAllocationMethod>;

export const PaymentAllocation = z.object({
  id: UuidString,
  payment_id: UuidString,
  invoice_id: UuidString,
  amount: MoneyAmount,
  allocated_at: IsoDateTime,
  allocated_by: UuidString,
  method: PaymentAllocationMethod,
});
export type PaymentAllocation = z.infer<typeof PaymentAllocation>;
